import { loadFacilityOptions, loadPatientOptions } from '@api/loadOptions'
import { genderOptions } from '@data'
import { useArchives, usePatient } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { PencilIcon } from '@icons'
import type { ArchiveFolderCreatePayload } from '@type/archive.types'
import { DateInputField, FormButton, FormInputField, Modal, SelectInputField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import moment from 'moment'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import ArchiveStepper from './ArchiveStepper'
import { DIGITIZATION_OPTIONS, STEP_FIELDS, type AddArchiveRecordFormValues } from './archiveRecordForm'

type Props = {
  onClose: () => void
}

const archiveRecordSchema = yup.object({
  patient_id: yup.string().required('Patient is required'),
  patient_name: yup.string().default(''),
  date_of_birth: yup.string().default(''),
  gender: yup.string().default(''),
  mrn: yup.string().default(''),
  folder_number: yup.string().required('Folder number is required'),
  date_range_start: yup.string().required('Start date is required'),
  date_range_end: yup.string().required('End date is required'),
  digitization_status: yup
    .string()
    .oneOf(['physical_only', 'partially_scanned', 'fully_scanned'])
    .required('Digitization status is required'),
  physical_location_name: yup.string().required('Archive room is required'),
  shelf: yup.string().required('Shelf is required'),
  column: yup.string().required('Cabinet is required'),
  section: yup.string().required('File box is required'),
  facility_id: yup.string().required('Department is required')
})

const defaultValues: AddArchiveRecordFormValues = {
  patient_id: '',
  patient_name: '',
  date_of_birth: '',
  gender: '',
  mrn: '',
  folder_number: '',
  date_range_start: '',
  date_range_end: '',
  digitization_status: 'physical_only',
  physical_location_name: '',
  shelf: '',
  column: '',
  section: '',
  facility_id: ''
}

const formatDisplayDate = (value: string) => (value ? moment(value).format('MMMM D, YYYY') : '—')

const formatGender = (value: string) => genderOptions.find(option => option.value === value)?.name || value || '—'

type ReviewSectionProps = {
  title: string
  onEdit: () => void
  rows: { label: string; value: string }[]
}

const ReviewSection = ({ title, onEdit, rows }: ReviewSectionProps) => (
  <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
    <div className="mb-3 flex items-center justify-between">
      <h4 className="text-xs font-semibold tracking-wide text-[#6B7280]">{title}</h4>
      <button className="flex items-center gap-1 text-xs text-[#374151]" onClick={onEdit} type="button">
        <PencilIcon />
        Edit
      </button>
    </div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {rows.map(row => (
        <div key={row.label}>
          <p className="text-xs text-[#9CA3AF]">{row.label}</p>
          <p className="text-sm font-medium text-[#111827]">{row.value || '—'}</p>
        </div>
      ))}
    </div>
  </div>
)

const AddArchiveRecord = ({ onClose }: Props) => {
  const [step, setStep] = useState(1)
  const [facilityLabel, setFacilityLabel] = useState('')
  const { createArchiveFolder } = useArchives({ enabled: false })
  const methods = useForm<AddArchiveRecordFormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(archiveRecordSchema)
  })

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    reset,
    formState: { isSubmitting }
  } = methods

  const patientId = watch('patient_id')
  const digitizationStatus = watch('digitization_status')
  const formValues = watch()

  const { singlePatient } = usePatient({ id: patientId })

  useEffect(() => {
    if (!singlePatient?.attributes) return
    const { date_of_birth, first_name, gender, last_name, ref } = singlePatient.attributes
    setValue('date_of_birth', date_of_birth || '')
    setValue('gender', gender || '')
    setValue('mrn', ref || '')
    setValue('patient_name', [first_name, last_name].filter(Boolean).join(' '))
  }, [singlePatient, setValue])

  const genderLabel = useMemo(() => formatGender(formValues.gender), [formValues.gender])

  const digitizationLabel = useMemo(
    () => DIGITIZATION_OPTIONS.find(option => option.value === digitizationStatus)?.label || '—',
    [digitizationStatus]
  )

  const handleContinue = async () => {
    const fields = STEP_FIELDS[step]
    const isValid = await trigger(fields)
    if (isValid) setStep(current => Math.min(current + 1, 4))
  }

  const onSubmit = async (formVal: AddArchiveRecordFormValues) => {
    // Only persist from the review step — Continue must never create a record
    if (step !== 4) return

    const payload: ArchiveFolderCreatePayload = {
      data: {
        type: 'archive_folder',
        attributes: {
          folder_number: formVal.folder_number,
          date_range_start: formVal.date_range_start,
          date_range_end: formVal.date_range_end,
          digitization_status: formVal.digitization_status,
          physical_location_name: formVal.physical_location_name,
          shelf: formVal.shelf,
          column: formVal.column,
          section: formVal.section,
          patient_id: Number(formVal.patient_id),
          facility_id: Number(formVal.facility_id)
        }
      }
    }

    try {
      await createArchiveFolder(payload)
      showToast('Archive record created successfully')
      reset(defaultValues)
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (step < 4) {
      void handleContinue()
      return
    }
    void handleSubmit(onSubmit)()
  }

  const footerOnClose = step === 1 ? onClose : () => setStep(current => current - 1)

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} style={{ minWidth: 'min(640px, calc(100vw - 1.5rem))' }} title="Add New Record">
        <form className="flex h-full flex-col overflow-auto" onSubmit={handleFormSubmit}>
          <ArchiveStepper currentStep={step} />

          <div className="flex-1 overflow-y-auto px-8 py-6 md:w-[680px] w-full">
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <Controller
                  control={control}
                  name="patient_id"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      borderRadius="100px"
                      defaultOptions
                      error={error?.message}
                      getOptionLabel={({ label }) => label}
                      getOptionValue={({ value }) => value}
                      isAsync
                      label="Patient Name"
                      loadOptions={loadPatientOptions}
                      onChange={(selectedOption: any) => {
                        onChange(selectedOption?.value || '')
                        setValue('patient_name', selectedOption?.label || '')
                      }}
                      placeholder="Enter patient's name"
                      value={value ? { value, label: formValues.patient_name || value } : undefined}
                    />
                  )}
                />
                <DateInputField disabled label="Date of Birth" name="date_of_birth" placeholder="mm/dd/yyyy" />
                <SelectInputField
                  disabled
                  label="Gender"
                  labelKey="name"
                  name="gender"
                  options={genderOptions}
                  placeholder="Select a gender"
                  valueKey={{ key: 'value', reduce: true }}
                />
                <div>
                  <FormInputField label="MRN (optional)" name="mrn" placeholder="E.g GH/4421" />
                  <p className="mt-1 text-xs text-[#9CA3AF]">
                    If the patient is not yet in the EMR, leave MRN blank. You can link it later once a match is
                    confirmed.
                  </p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4">
                <FormInputField label="Old folder number" name="folder_number" placeholder="E.g GH/4421" />
                <FormInputField label="Date Range Start" name="date_range_start" type="date" />
                <FormInputField label="Date Range End" name="date_range_end" type="date" />
                <div>
                  <p className="mb-2 text-sm font-medium text-[#374151]">
                    How much of this file has been digitised so far?
                  </p>
                  <div className="flex rounded-full border border-[#E5E7EB] bg-[#F9FAFB] p-1">
                    {DIGITIZATION_OPTIONS.map(option => (
                      <button
                        className={`flex-1 rounded-full px-3 py-2 text-xs font-medium transition-colors sm:text-sm ${
                          digitizationStatus === option.value
                            ? 'bg-[#F5F0E6] text-[#111827] shadow-sm'
                            : 'text-[#6B7280]'
                        }`}
                        key={option.value}
                        onClick={() => setValue('digitization_status', option.value, { shouldValidate: true })}
                        type="button"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-4">
                <FormInputField label="Archive Room" name="physical_location_name" placeholder="E.g Archive A" />
                <FormInputField label="Shelf" name="shelf" placeholder="E.g S-04" />
                <FormInputField label="Cabinet" name="column" placeholder="E.g C-04" />
                <FormInputField label="File Box" name="section" placeholder="E.g C-04" />
                <Controller
                  control={control}
                  name="facility_id"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      borderRadius="100px"
                      defaultOptions
                      error={error?.message}
                      getOptionLabel={({ label }) => label}
                      getOptionValue={({ value }) => value}
                      isAsync
                      label="Facility"
                      loadOptions={loadFacilityOptions}
                      onChange={(selectedOption: any) => {
                        onChange(selectedOption?.value || '')
                        setFacilityLabel(selectedOption?.label || '')
                      }}
                      placeholder="Select facility"
                      value={value ? { value, label: facilityLabel || value } : undefined}
                    />
                  )}
                />
              </div>
            )}

            {step === 4 && (
              <div className="flex flex-col gap-4">
                <ReviewSection
                  onEdit={() => setStep(1)}
                  rows={[
                    { label: 'Full Name', value: formValues.patient_name },
                    { label: 'Date of Birth', value: formatDisplayDate(formValues.date_of_birth) },
                    { label: 'Gender', value: genderLabel },
                    { label: 'Hospital Number', value: formValues.mrn },
                    { label: 'MRN', value: formValues.mrn }
                  ]}
                  title="PATIENT"
                />
                <ReviewSection
                  onEdit={() => setStep(2)}
                  rows={[
                    { label: 'Old File Number', value: formValues.folder_number },
                    { label: 'Date Range Start', value: formatDisplayDate(formValues.date_range_start) },
                    { label: 'Date Range End', value: formatDisplayDate(formValues.date_range_end) },
                    { label: 'Digitization Status', value: digitizationLabel }
                  ]}
                  title="FILE INFO"
                />
                <ReviewSection
                  onEdit={() => setStep(3)}
                  rows={[
                    { label: 'Room', value: formValues.physical_location_name },
                    { label: 'Shelf', value: formValues.shelf },
                    { label: 'Cabinet', value: formValues.column },
                    { label: 'Box', value: formValues.section }
                  ]}
                  title="LOCATION & OWNERSHIP"
                />
              </div>
            )}
          </div>

          <FormButton
            cancel={step === 1 ? 'Cancel' : 'Back'}
            isLoading={isSubmitting}
            onClose={footerOnClose}
            submit={step === 4 ? 'Save Record' : 'Continue'}
            type="submit"
          />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default AddArchiveRecord
